"use client";
// "use client" hoja interactiva: canvas 2D con rAF + IntersectionObserver/ResizeObserver — imposible en server.

// 2026-09-07 HeroAmbientCanvas v5 — réplica de las OLAS-MONTAÑA de Lovable:
// crestas redondeadas que suben alto en los bordes izquierdo/derecho y bajan en
// un valle central limpio (perfil parabólico suavizado), bandas azules laterales
// y fondo del token --background. Investigación 2026-09-07: Codrops "Ambient Canvas
// Backgrounds" (capas de ondas + rAF + blur pesado = aurora), Medium/design-
// bootcamp Caden Chen (mesh animado en canvas supera a CSS animado), Chrome
// DevRel "Animating a blur" (el blur NUNCA se anima: se animan las rutas y el
// filter:blur() CSS queda estático en GPU), MDN "Optimizing canvas" (cap DPR).
// v5.3 (feedback dueño): SIMETRÍA EXACTA L/R — el oleaje se calcula en la
// coordenada espejo e=|x-0.5| con modos coseno (idéntico en x y 1-x en todo
// instante) y los glows cálidos van en pares espejados. Las montañas viven en
// las esquinas INFERIORES (crestas 0.34/0.42/0.5) + un poquito de rosa en el
// centro inferior (valle trasero 0.7), como el source. Contraste: la zona del
// titular/composer lleva lavado radial + franja superior. Perf: ~3 paths + 4
// gradientes por frame, cap 30fps, DPR ≤1.5, pausa fuera de pantalla y con
// pestaña oculta, frame único estático con prefers-reduced-motion. El blur
// v5.4 (fix real del "cosito" superior-izquierdo): el canvas es elemento
// reemplazado — con solo `-inset-[6%]` el navegador ignora right/bottom
// (CSS 2.1 §10.3.8) y lo dejaba en 300×150 en la esquina, con el bitmap
// estirado ~6× y remuestreo subpíxel asimétrico. Ahora h-[112%] w-[112%]
// explícitos + ResizeObserver sobre el propio canvas (se autocorrige si el
// CSS llega después del mount). Backing verificado 1:1 con la caja CSS.

import { Component, useEffect, useRef, type ReactNode } from "react";
// 2026-09-20 FASE 3 P1 (Lovable-hero SSOT, research R3 2026 canvas + CSS vars): la paleta vive en
// HERO_TOKENS (espejo TS de hero-tokens.css) y el fondo en --background — ningún hex literal de
// marca fuera del SSOT en este archivo. Los tintes rosa se precalculan en JS (mixHex hex→hex,
// canvas-safe 2026-09-20 P0 Sentry 1S/1T) — nunca color-mix() en addColorStop.
import { HERO_TOKENS } from "@/lib/design/hero-tokens";

/** Cresta de cordillera: alta en los bordes, valle suave al centro. */
type Ridge = {
  /** gradiente vertical (0 = arriba del lienzo, 1 = borde inferior) */
  stops: Array<[number, string]>;
  /** altura de la cresta en los bordes (fracción de H, desde arriba) */
  crestEdge: number;
  /** altura del valle central (fracción de H) */
  valley: number;
  /** exponente del perfil — >1 = "montañita" con hombro redondeado */
  power: number;
  /** armónicos de la oleada (amplitud en fracción de H, solo en las crestas) */
  amp1: number;
  amp2: number;
  freq1: number;
  freq2: number;
  sp1: number;
  sp2: number;
  ph: number;
};

// 2026-09-23 FASE 3 FIX diseño system-tokens (research 2026 W3C DTCG warm-paper SSOT:
// fallbacks = mismos tokens --background light #f3efe8 / dark #0f0f14 de globals.css,
// nunca blancos #fcfbf8 ni negros #131318 que bifurcan superficies).
const BASE_FALLBACK_LIGHT = "#f3efe8";
const BASE_FALLBACK_DARK = "#0f0f14";

/** Fondo del canvas = token --background (SSOT tema); fallback solo si el var no es hex.
 * 2026-09-26 perf (research 2026-09-26 MDN getComputedStyle + Chrome DevRel:
 * getComputedStyle fuerza style-recalc sincrónico — llamarlo por frame a 30fps
 * es layout-thrash; se lee solo en resolveHeroTheme (themechange/resize),
 * nunca en paint/loop). */
function themeBackground(dark: boolean): string {
  try {
    const v = getComputedStyle(document.documentElement)
      .getPropertyValue("--background")
      .trim();
    if (/^#[0-9a-fA-F]{6}$/.test(v)) return v;
  } catch {}
  return dark ? BASE_FALLBACK_DARK : BASE_FALLBACK_LIGHT;
}

/** Mezcla HEX en JS (canvas-safe).
 * 2026-09-20 P0 Sentry JAVASCRIPT-NEXTJS-1S/1T (research 2026-09-20 MDN CanvasGradient.addColorStop + Chrome canvas color parsing):
 * `color-mix(in srgb, ...)` es válido en CSS pero CanvasGradient lo rechaza en varios Chrome/Edge
 * (`Failed to execute 'addColorStop' ... could not be parsed as a color`, 4 eventos/1 usuario).
 * Se precalcula el mix en hex en JS — mismo resultado visual, 0 SyntaxError. CSS color-mix intacto. */
export function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  const full =
    h.length === 3
      ? h
          .split("")
          .map((c) => c + c)
          .join("")
      : h;
  const n = parseInt(full.slice(0, 6), 16);
  if (!Number.isFinite(n)) return [255, 255, 255];
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function rgbToHex(r: number, g: number, b: number): string {
  const cl = (v: number) => Math.max(0, Math.min(255, Math.round(v)));
  return `#${((cl(r) << 16) | (cl(g) << 8) | cl(b)).toString(16).padStart(6, "0")}`;
}

export function mixHex(a: string, pctA: number, b: string): string {
  const t = Math.max(0, Math.min(100, pctA)) / 100;
  const [ar, ag, ab] = hexToRgb(a);
  const [br, bg, bb] = hexToRgb(b);
  return rgbToHex(
    ar * t + br * (1 - t),
    ag * t + bg * (1 - t),
    ab * t + bb * (1 - t),
  );
}

/** Tinte derivado de un token hero hacia blanco/negro (cordilleras = aclarados de --hero-magenta). */
export function heroTint(
  color: string,
  pct: number,
  target: "white" | "black" = "white",
): string {
  return mixHex(color, pct, target === "white" ? "#ffffff" : "#000000");
}

/** Mezcla entre dos tokens hero (transiciones de la rampa). */
export function heroMix(a: string, pctA: number, b: string): string {
  return mixHex(a, pctA, b);
}

/** Añade un stop sin reventar el frame si el color no parsea.
 * 2026-09-21 FASE 5 P1 (research 2026-09-21 MDN CanvasGradient.addColorStop +
 * Chromium CanvasGradientCSSColor4 + WPT 2d.gradient.colormix): addColorStop
 * lanza SyntaxError si el color no parsea como CSS <color>, y el soporte de
 * `color-mix()` en canvas es inconsistente entre navegadores (en Chromium el
 * Color 4 de CanvasGradient vive tras la flag experimental CanvasGradientCSSColor4,
 * spec WHATWG html#10986 aún no asentada — CSS color-mix ≠ canvas color-mix).
 * Los stops ya se pre-resuelven a hex en JS (mixHex/heroTint/heroMix), pero un
 * token futuro no-hex no debe tumbar el frame entero: un stop malo se salta y
 * el gradiente pinta con el resto. Retorna false si el stop se descartó. */
export function safeAddColorStop(
  grad: { addColorStop: (offset: number, color: string) => void },
  offset: number,
  color: string,
): boolean {
  try {
    grad.addColorStop(offset, color);
    return true;
  } catch {
    return false;
  }
}

const BLUE = HERO_TOKENS.blue;

/** 3 cordilleras rosas (atrás → frente) + glows cálidos, rampa hero SSOT:
    blue → magenta → red → orange (HERO_TOKENS). Los rosas claros son aclarados
    de --hero-magenta vía color-mix (ver heroTint), no hex literales.
    v5.3: freq* están en la coordenada espejo e (0 centro → 1 borde) — la
    simetría L/R es exacta por construcción. Crestas en las esquinas
    inferiores; el valle trasero deja "un poquito" de rosa al centro abajo. */
const RIDGES: Ridge[] = [
  {
    stops: [
      [0, heroTint(HERO_TOKENS.magenta, 45)],
      [0.45, heroTint(HERO_TOKENS.magenta, 60)],
      [1, heroTint(HERO_TOKENS.magenta, 75)],
    ],
    crestEdge: 0.34,
    valley: 0.78,
    power: 1.5,
    amp1: 0.055,
    amp2: 0.032,
    freq1: 2.0,
    freq2: 4.6,
    sp1: 0.0011,
    sp2: 0.0016,
    ph: 0.4,
  },
  {
    stops: [
      [0, heroTint(HERO_TOKENS.magenta, 60)],
      [0.5, heroTint(HERO_TOKENS.magenta, 80)],
      [1, HERO_TOKENS.magenta],
    ],
    crestEdge: 0.42,
    valley: 0.88,
    power: 1.42,
    amp1: 0.063,
    amp2: 0.037,
    freq1: 1.7,
    freq2: 4.0,
    sp1: 0.0009,
    sp2: 0.0014,
    ph: 2.6,
  },
  {
    stops: [
      [0, heroTint(HERO_TOKENS.magenta, 80)],
      [0.45, HERO_TOKENS.magenta],
      [0.85, heroMix(HERO_TOKENS.magenta, 55, HERO_TOKENS.red)],
      [1, HERO_TOKENS.red],
    ],
    crestEdge: 0.5,
    valley: 0.95,
    power: 1.36,
    amp1: 0.072,
    amp2: 0.042,
    freq1: 1.4,
    freq2: 3.6,
    sp1: 0.00075,
    sp2: 0.0012,
    ph: 4.8,
  },
];

function isDark(): boolean {
  if (typeof document === "undefined") return false;
  const t = document.documentElement.getAttribute("data-theme");
  if (t) return t === "dark";
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
}

/** Tema cacheado del hero (2026-09-26 perf SEED e8851c1e: getComputedStyle +
 *  matchMedia solo en themechange/resize, nunca por frame — research 2026
 *  MDN getComputedStyle sync-recalc + Google INP: el rAF a 30fps no puede
 *  forzar style-recalc; paint() recibe el tema ya resuelto). */
export type HeroTheme = { dark: boolean; base: string };
export function resolveHeroTheme(): HeroTheme {
  const dark = isDark();
  return { dark, base: themeBackground(dark) };
}

function paint(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  time: number,
  theme: HeroTheme,
  transparent = false,
) {
  // 2026-09-20 FASE 3 P1: el fondo es el token --background (SSOT tema), no un hex fijo.
  // 2026-09-26 perf: tema ya resuelto por el caller (resolveHeroTheme en
  // themechange/resize) — paint nunca toca getComputedStyle/matchMedia/DOM.
  const base = theme.base;
  const dark = theme.dark;
  if (!transparent) {
    ctx.fillStyle = base;
    ctx.fillRect(0, 0, w, h);
  } else {
    ctx.clearRect(0, 0, w, h);
  }

  // 2026-10-27 craft overhaul: whisper-soft studio depth, eliminating AI-slop neon saturation
  const dim = dark ? 0.35 : 0.5;

  // Bandas laterales — profundidad arquitectónica serena y sutil (no manchas estridentes)
  const blueAlpha = 0.22 * dim;
  const sway = Math.sin(time * 0.0007) * 0.015 * w;
  const bandCy = (transparent ? 0.78 : 0.62) * h;
  const drawBlueBand = (cx: number) => {
    const cy = bandCy;
    const rx = 0.3 * w;
    const ry = 0.52 * h;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.scale(1, ry / rx);
    const g = ctx.createRadialGradient(0, 0, 0, 0, 0, rx);
    safeAddColorStop(
      g,
      0,
      `${BLUE}${Math.round(blueAlpha * 255)
        .toString(16)
        .padStart(2, "0")}`,
    );
    safeAddColorStop(g, 1, `${BLUE}00`);
    ctx.fillStyle = g;
    ctx.fillRect(-rx, -rx, rx * 2, rx * 2);
    ctx.restore();
  };
  drawBlueBand(-0.02 * w - sway);
  drawBlueBand(1.02 * w + sway);

  // Iluminación cálida ambiental — sutil velo de estudio (baja saturación)
  const warm = dark ? 0.06 : 0.1;
  const glow = (cx: number, cy: number, r: number, color: string) => {
    const g = ctx.createRadialGradient(
      cx * w,
      cy * h,
      0,
      cx * w,
      cy * h,
      r * w,
    );
    safeAddColorStop(
      g,
      0,
      `${color}${Math.round(warm * 255)
        .toString(16)
        .padStart(2, "0")}`,
    );
    safeAddColorStop(g, 1, `${color}00`);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);
  };
  glow(0.08, 1.03, 0.32, HERO_TOKENS.orange);
  glow(0.92, 1.03, 0.32, HERO_TOKENS.orange);
  glow(0.3, 1.07, 0.26, HERO_TOKENS.red);
  glow(0.7, 1.07, 0.26, HERO_TOKENS.red);

  // Cordilleras — profundidad sutil con transparencia atenuada (anti vibe-coding / anti-slop)
  const SEGMENTS = 56;
  ctx.save();
  ctx.globalAlpha = dark ? 0.3 : 0.25;
  for (const r of RIDGES) {
    const grad = ctx.createLinearGradient(0, r.crestEdge * h * 0.6, 0, h);
    for (const [pos, color] of r.stops) safeAddColorStop(grad, pos, color);
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.moveTo(0, h + 2);
    for (let i = 0; i <= SEGMENTS; i++) {
      const x = i / SEGMENTS;
      const e = Math.abs(x - 0.5) * 2; // 0 centro → 1 borde (espejo exacto)
      const profile = Math.pow(e, r.power);
      const wave =
        Math.cos(Math.PI * r.freq1 * e + time * r.sp1 + r.ph) * r.amp1 +
        Math.cos(Math.PI * r.freq2 * e - time * r.sp2 + r.ph * 1.7) * r.amp2;
      // respiración global lenta de toda la cresta (también espejada)
      const breathe = Math.cos(time * 0.0004 + r.ph) * 0.02;
      let yFrac =
        r.valley -
        (r.valley - r.crestEdge) * profile +
        (wave + breathe) * profile;
      if (transparent) yFrac = 1 - (1 - yFrac) * 0.72;
      const y = yFrac * h;
      if (i === 0) ctx.lineTo(0, y);
      else ctx.lineTo(x * w, y);
    }
    ctx.lineTo(w, h + 2);
    ctx.closePath();
    ctx.fill();
  }
  ctx.restore();

  // Lavado central: protege SOLO la zona del titular + composer (queja
  // original: "estorba el texto"). v5.1: antes washMax 0.92 en claro mataba
  // las olas casi por completo — ahora cubre menos radio y menos opacidad;
  // en el valle central las cordilleras ya bajan solas, no hace falta tapiar.
  // En modo transparente se omite (pintaría un parche del color base sobre
  // la superficie que lo hospeda).
  if (!transparent) {
    const wash = ctx.createRadialGradient(
      0.5 * w,
      0.46 * h,
      0,
      0.5 * w,
      0.46 * h,
      Math.max(0.42 * w, 0.44 * h),
    );
    const washMax = dark ? 0.78 : 0.6;
    safeAddColorStop(
      wash,
      0,
      `${base}${Math.round(washMax * 255)
        .toString(16)
        .padStart(2, "0")}`,
    );
    safeAddColorStop(
      wash,
      0.55,
      `${base}${Math.round(washMax * 0.45 * 255)
        .toString(16)
        .padStart(2, "0")}`,
    );
    safeAddColorStop(wash, 1, `${base}00`);
    ctx.fillStyle = wash;
    ctx.fillRect(0, 0, w, h);

    // Franja superior — AA del titular. v5.1: más corta (0.27h) y menos opaca
    // en claro (0.82): las crestas laterales suben hasta 0.34h y deben verse.
    const top = ctx.createLinearGradient(0, 0, 0, 0.27 * h);
    const topMax = dark ? 0.85 : 0.82;
    safeAddColorStop(
      top,
      0,
      `${base}${Math.round(topMax * 255)
        .toString(16)
        .padStart(2, "0")}`,
    );
    safeAddColorStop(top, 1, `${base}00`);
    ctx.fillStyle = top;
    ctx.fillRect(0, 0, w, 0.27 * h);
  }
}

export function HeroAmbientCanvas({
  className,
  transparent = false,
  animate,
}: {
  /** Geometría del lienzo. OJO: el canvas es elemento reemplazado — siempre
   *  incluir w/h explícitos (los insets opposing solos lo dejan en 300×150). */
  className?: string;
  /** Solo cordilleras + glows, sin fondo ni lavados: para reutilizar la ola
   *  del hero en bookends, cards y páginas (modo migración 2026-09-08). */
  transparent?: boolean;
  /** 2026-09-23 FASE 2 F-04 (research 2026 canvas perf + free-tier CPU budget:
   *  frequency × instances — el landing montaba 3+ lienzos a 30fps; por defecto
   *  solo el hero (opaco) anima, los transparentes pintan 1 frame estático.
   *  Pasar `animate` explícito para forzar el modo. */
  animate?: boolean;
} = {}) {
  return (
    <HeroCanvasErrorBoundary>
      <HeroAmbientCanvasInner
        className={className}
        transparent={transparent}
        animate={animate ?? !transparent}
      />
    </HeroCanvasErrorBoundary>
  );
}

/** Error boundary decorativo (2026-09-21 FASE 5 P1 Sentry 1S/1T): si el render
 *  del canvas alguna vez lanza (token/gradiente futuro), se desmonta en silencio
 *  y queda el fondo CSS — nunca una tarjeta de error en el hero. Los errores de
 *  paint en efecto ya se tragan por-stop (safeAddColorStop) y por-frame (try/catch). */
class HeroCanvasErrorBoundary extends Component<
  { children: ReactNode },
  { failed: boolean }
> {
  override state = { failed: false };
  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }
  override componentDidCatch(): void {
    // Decorativo: sin reporte — el fondo CSS cubre la ausencia.
  }
  override render(): ReactNode {
    if (this.state.failed) return null;
    return this.props.children;
  }
}

function HeroAmbientCanvasInner({
  className,
  transparent = false,
  animate = !transparent,
}: {
  className?: string;
  transparent?: boolean;
  animate?: boolean;
} = {}) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let raf = 0;
    let running = true;
    let lastPaint = 0;
    // 2026-09-26 perf SEED e8851c1e: tema cacheado — getComputedStyle +
    // matchMedia solo aquí (themechange/resize), nunca por frame. research
    // 2026 MDN getComputedStyle sync-recalc.
    let cachedTheme = resolveHeroTheme();
    const refreshTheme = () => {
      try {
        cachedTheme = resolveHeroTheme();
      } catch {
        // noop decorativo: se conserva el último tema válido
      }
    };

    const resize = () => {
      // clientWidth/Height = tamaño CSS real (con insets ya aplicados).
      // ResizeObserver (no solo window resize): en dev el CSS de Tailwind
      // puede llegar DESPUÉS del mount — medir una vez dejaba el backing
      // en 300×150 y el navegador estiraba el bitmap (~6×) con blobs
      // asimétricos por el remuestreo subpíxel. El RO se autocorrige.
      // 2026-10-11 perf móvil (research 2026 SO "canvas DPR kills performance"
      // + MDN devicePixelRatio 2026-08-21: el backing escala con DPR² — en
      // viewports <768px o con Save-Data el lienzo es decorativo y DPR 1
      // recorta ~56% de píxeles vs 1.5 sin cambio visible; desktop intacto).
      // 2026-10-11 fix Save-Data (research 2026 MDN NetworkInformation.saveData
      // + Navigator.connection vía TinyFish 2026-10-11: Save-Data vive en
      // navigator.connection, NO en navigator.saveData — el chequeo anterior
      // nunca era true y el desktop con ahorro de datos pagaba DPR 1.5;
      // paridad con film-data-saver.ts que ya lee connection.saveData).
      // 2026-10-11 paridad 2G (research 2026 MDN NetworkInformation.effectiveType
      // vía TinyFish 2026-10-11 slow-2g/2g + film-data-saver.ts SLOW_EFFECTIVE_TYPES:
      // el canvas es decorativo y en 2G el DPR 1 ahorra el mismo ~56% de píxeles
      // que en Save-Data; 3g/4g intactos).
      const connection =
        typeof navigator !== "undefined"
          ? (
              navigator as Navigator & {
                connection?: {
                  saveData?: boolean;
                  effectiveType?: string;
                };
              }
            ).connection
          : undefined;
      const effectiveType = (connection?.effectiveType ?? "")
        .trim()
        .toLowerCase();
      const lowPower =
        window.innerWidth < 768 ||
        connection?.saveData === true ||
        effectiveType === "slow-2g" ||
        effectiveType === "2g";
      const dprCap = lowPower ? 1 : 1.5;
      const dpr = Math.min(window.devicePixelRatio || 1, dprCap);
      const w = canvas.clientWidth || canvas.parentElement?.clientWidth || 1;
      const h = canvas.clientHeight || canvas.parentElement?.clientHeight || 1;
      canvas.width = Math.max(1, Math.round(w * dpr));
      canvas.height = Math.max(1, Math.round(h * dpr));
      // El resize puede traer nuevo CSS (tokens) — re-resuelve el tema aquí.
      refreshTheme();
    };
    resize();
    // Observar el PROPIO canvas: su caja CSS cambia al llegar el CSS de
    // Tailwind en dev y con zoom/viewport — el padre no cambia en esos casos.
    // Sin bucle: fijar width/height (atributos) no altera la caja CSS (la
    // constriñen los insets), así que el RO no se re-dispara por el resize.
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const reduced =
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

    // F-04: instancias decorativas (transparent/bookends) pintan 1 frame
    // estático y repintan solo en resize — 0 rAF, 0 wakeups.
    if (!animate || reduced) {
      const paintStatic = () => {
        // 2026-09-20 P0 Sentry hardening: paint decorativo nunca revienta la página —
        // si un color/token futuro falla, se deja el fondo CSS y se silencia.
        try {
          paint(
            ctx,
            canvas.width,
            canvas.height,
            24000,
            cachedTheme,
            transparent,
          );
        } catch {
          // noop decorativo
        }
      };
      paintStatic();
      // En estático el resize del backing borra el bitmap: repintar tras medir.
      const roStatic = new ResizeObserver(() => {
        resize();
        paintStatic();
      });
      roStatic.observe(canvas);
      ro.disconnect();
      return () => roStatic.disconnect();
    }

    // Cap 30fps: las olas son lentas (períodos de ~20-45 s); 30 fps las
    // representa idéntico y deja CPU libre (regla free-tier CPU budget).
    const FRAME_MS = 1000 / 30;
    const loop = (t: number) => {
      if (!running) return;
      if (t - lastPaint >= FRAME_MS) {
        lastPaint = t;
        // 2026-09-20 P0 Sentry hardening: un throw en un frame no mata el rAF —
        // se salta el frame y se sigue (el canvas es decorativo).
        // 2026-09-26 perf: tema cacheado — 0 DOM por frame.
        try {
          paint(ctx, canvas.width, canvas.height, t, cachedTheme, transparent);
        } catch {
          // noop decorativo
        }
      }
      raf = requestAnimationFrame(loop);
    };

    // 2026-09-11 perf (websearch 2026: Wensity 2026-06-17 fix-LCP-first +
    // Vercel LCP 4 stages — el gap TTFB→FCP es contención de render): pintar
    // un frame estático de inmediato (el hero se ve terminado para el LCP) y
    // arrancar la ola rAF solo cuando el navegador esté idle. El canvas
    // animaba a 30fps desde el mount compitiendo con el pintado del texto LCP
    // en viewports desktop grandes (Speed Insights desktop LCP 5.44s vs
    // mobile 1.32s). IO + visibility gates se mantienen.
    // 2026-09-20 P0 Sentry hardening: primer frame también protegido.
    try {
      paint(ctx, canvas.width, canvas.height, 24000, cachedTheme, transparent);
    } catch {
      // noop decorativo
    }
    let idleReady = false;
    let fontsReady = false;
    let started = false;
    const start = () => {
      // 2026-10-11 LCP (research 2026 next/font display:swap + font CLS: el
      // rAF compitiendo con el swap de la fuente mueve el titular — se arranca
      // solo con idle + document.fonts.ready; el frame estático de arriba ya
      // cubre el LCP mientras tanto).
      if (started || !running || !idleReady || !fontsReady) return;
      started = true;
      lastPaint = 0;
      raf = requestAnimationFrame(loop);
    };
    const onIdle = () => {
      idleReady = true;
      start();
    };
    let idleHandle: number | NodeJS.Timeout | undefined;
    let idleCancelled = false;
    if (typeof window.requestIdleCallback !== "undefined") {
      const ric = window.requestIdleCallback(onIdle, { timeout: 2500 });
      idleHandle = ric;
    } else {
      idleHandle = setTimeout(onIdle, 1200);
    }
    // document.fonts.ready además de onIdle (ver start arriba).
    try {
      if (
        typeof document !== "undefined" &&
        typeof document.fonts?.ready?.then === "function"
      ) {
        document.fonts.ready.then(() => {
          fontsReady = true;
          start();
        });
      } else {
        fontsReady = true;
      }
    } catch {
      fontsReady = true;
    }

    const io = new IntersectionObserver((entries) => {
      running = (entries[0]?.isIntersecting ?? true) && !document.hidden;
      if (running) {
        if (idleReady) {
          if (!started) start();
          else {
            lastPaint = 0;
            raf = requestAnimationFrame(loop);
          }
        }
      }
    });
    io.observe(canvas);
    const onVis = () => {
      running = !document.hidden;
      if (running && idleReady && !started) {
        start();
      } else if (running && idleReady && started) {
        lastPaint = 0;
        raf = requestAnimationFrame(loop);
      }
    };
    document.addEventListener("visibilitychange", onVis);
    // 2026-09-26 perf: el tema solo se re-resuelve aquí (OS scheme +
    // data-theme), nunca en el loop. Resize ya refresca vía resize().
    const schemeMq =
      typeof window.matchMedia === "function"
        ? window.matchMedia("(prefers-color-scheme: dark)")
        : null;
    const onScheme = () => refreshTheme();
    schemeMq?.addEventListener?.("change", onScheme);
    const themeMo =
      typeof MutationObserver !== "undefined"
        ? new MutationObserver(refreshTheme)
        : null;
    try {
      themeMo?.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ["data-theme", "class", "style"],
      });
    } catch {
      // noop decorativo
    }

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      io.disconnect();
      ro.disconnect();
      document.removeEventListener("visibilitychange", onVis);
      schemeMq?.removeEventListener?.("change", onScheme);
      themeMo?.disconnect();
      if (!idleCancelled) {
        idleCancelled = true;
        if (
          typeof window.cancelIdleCallback !== "undefined" &&
          typeof idleHandle === "number"
        ) {
          window.cancelIdleCallback(idleHandle);
        } else if (idleHandle !== undefined) {
          clearTimeout(idleHandle as NodeJS.Timeout);
        }
      }
    };
  }, [transparent, animate]);

  return (
    <canvas
      ref={ref}
      aria-hidden="true"
      // w/h EXPLÍCITOS: el canvas es elemento reemplazado — con solo insets
      // opposing el navegador ignora right/bottom y lo deja en 300×150
      // (CSS 2.1 §10.3.8). h/w 112% + inset -6% = cubre el hero con overscan
      // para el desvanecido del blur.
      className={`hero-ambient-canvas hero-canvas-enter pointer-events-none ${
        className ?? "absolute -inset-[6%] h-[112%] w-[112%]"
      }`}
    />
  );
}
